import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { useAuth } from '../../contexts/AuthContext';
import { classesAPI } from '../../services/classService';
import { admissionsAPI } from '../../services/admissionService';
import { parentsAPI } from '../../services/parentService';
import { couponsAPI } from '../../services/couponService';
import AdmissionsTable from './AdmissionsTable';
import AdmissionDetails from './AdmissionDetails';
import ParentFormDialog from './ParentFormDialog';
import AdmissionFormDialog from './AdmissionFormDialog';
import { employeesAPI } from '../../services/employeesService';
import ProcessAdmissionDialog from './ProcessAdmissionDialog';
import ConfirmAdmission from './ConfirmAdmisson';

const Admissions = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const tId = localStorage.getItem('id')  // Assuming teacher ID is stored in localStorage under 'id' key. Adjust as needed.
  const [loading, setLoading] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [parents, setParents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [coupons, setCoupons] = useState([]);
    const [employees, setEmployees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showAdmissionConfirmDialog, setShowAdmissionConfirmDialog] = useState(false);
  const [showProcessAdmissionDialog, setShowProcessAdmissionDialog] = useState(false);


  // Lazy loading params for server-side pagination / filtering / sorting
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 4,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: '', matchMode: FilterMatchMode.EQUALS }
    }
  });

  // Dialog states
  const [parentDialog, setParentDialog] = useState(false);
  const [admissionDialog, setAdmissionDialog] = useState(false);
  const [showDetailsView, setShowDetailsView] = useState(false);

  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [editingParent, setEditingParent] = useState(null);
  const [editingAdmission, setEditingAdmission] = useState(null);
  const [detailsAdmission, setDetailsAdmission] = useState(null);
   const [student, setStudent] = useState(null);

  // Parent form state
  const initialParentForm = {
    firstName: '',
    // lastName: '',
    email: '',
    phone: '',
    address: ''
  };

  const [parentForm, setParentForm] = useState(initialParentForm);

  // Admission form state
  const initialAdmissionForm = {
    fullName: '',
    // lastName: '',
    admissionDate: new Date(),
    dateOfBirth: null,
    className:'',
    gender: '',
    classId: '',
    parentId: '',
    imageUrl: '',
    status: 'pending',
    teacherId: tId,
    couponId: null,
    bloodGroup:"",
    address: "",
    referralType:"",
    addmissionerId:tId,
    referralName:"",
    referralPhone:"",
  };

  const [admissionForm, setAdmissionForm] = useState(initialAdmissionForm);

  // Gender options
  const genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Other', value: 'other' }
  ];
  const referralOptions = [
    { label: 'Employee', value: 'employee' },
    { label: 'Other', value: 'other' }
  ];

  // Status options
  const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Admitted', value: 'admitted' },
    { label: 'Pending', value: 'pending' }
  ];

  // Helper function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  useEffect(() => {
    loadLazyData();
  }, [lazyParams]);

  const buildQueryParams = () => {
    const params = {
      page: lazyParams.page || 1,
      limit: lazyParams.rows || 10,
      search: lazyParams.filters?.global?.value || '',
      status: lazyParams.filters?.status?.value || ''
    };

    if (lazyParams.sortField) {
      params.sortField = lazyParams.sortField;
      params.sortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
    }

    return params;
  };

  const loadLazyData = async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const response = await admissionsAPI.getAdmissionsByParams(params);
      const admissionData = response.data.data.admissions || [];
      const paginationData = response.data.data.pagination || {};
      setAdmissions(admissionData);
      setTotalRecords(paginationData.totalRows || admissionData.length);
    } catch (err) {
      console.error('Failed to load admissions', err);
      setAdmissions([]);
      setTotalRecords(0);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load admissions' });
    } finally {
      setLoading(false);
    }
  };

  const loadParents = async () => {
    try {
      const response = await parentsAPI.getparents();
      const parents = response.data.data || [];
      setParents(parents);
    } catch (err) {
      console.error('Failed to load parents', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load parents' });
    }
  };

  const loadClasses = async () => {
    try {
      const response = await classesAPI.getAllclasses();
      const classes = response.data.data || [];
      setClasses(classes);
    } catch (err) {
      console.error('Failed to load classes', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load classes' });
    }
  };

  const loadCoupons = async () => {
    try {
      const res = await couponsAPI.getUnUsedCoupons();
      const couponsData = res?.data?.coupons || [];
      setCoupons(couponsData);
    } catch (err) {
      console.error('loadCoupons error', err);
    }
  };

  // DataTable event handlers
  const onPage = (event) => {
    setLazyParams((p) => ({ ...p, first: event.first, rows: event.rows, page: Math.floor(event.first / event.rows) + 1 }));
  };

  const onSort = (event) => {
    setLazyParams((p) => ({ ...p, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value || '';
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: { ...p.filters, global: { value, matchMode: FilterMatchMode.CONTAINS } } }));
  };

  const onStatusFilterChange = (value) => {
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: { ...p.filters, status: { value: value === '' ? null : value, matchMode: FilterMatchMode.EQUALS } } }));
  };

  // CRUD handlers
  const handleCreateParent = () => {
    setParentForm(initialParentForm);
    setEditingParent(null);
    setParentDialog(true);
  };

  const handleCreateAdmission = () => {
    loadParents();
    loadClasses();
    loadCoupons();
      loadEmployees();
    setAdmissionForm(initialAdmissionForm);
    setEditingAdmission(null);
    setAdmissionDialog(true);
  };
  
   const handleSaveAdmission = async () => {
      setLoading(true);
      try {
         if (editingAdmission) {
          await admissionsAPI.updateAdmission(editingAdmission.id, admissionForm);
          toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Admission updated' });
        } else {
          await admissionsAPI.createAdmission(admissionForm);
          toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Admission created' });
        }
    setAdmissionDialog(false);
        setEditingAdmission(null);
        loadLazyData();
      } catch (err) {
        console.error('save admission error', err);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save admission' });
      } finally {
        setLoading(false);
      }
    };
  const handleEditAdmission = (admission) => {
    loadParents();
    loadClasses();
    loadCoupons();
      loadEmployees();
    setEditingAdmission(admission);
    setAdmissionForm({
      fullName: admission.firstName || '',
      admissionNumber: admission.admissionNumber || '',
      dateOfBirth: admission.dateOfBirth || ''? new Date(admission.dateOfBirth) : null,
      gender: admission.gender || '',
      classId:admission.classId || '',
      admissionDate: admission.admissionDate || ''? new Date(admission.admissionDate) : null,
      imageUrl: admission.imageUrl || '',
      parentId: admission.parentId || '',
      teacherId: admission.teacherId || '',
      couponId: admission.couponId || '',
      address: admission.address || '',
      referralType: admission.referralType || '',
      addmissionerId:admission.addmissionerId || '',
      referralName: admission.referralName || '',
      referralPhone: admission.referralPhone || '',

      
    });

    setAdmissionDialog(true);
  };

  const handleSaveParent = async () => {
    setLoading(true);
    try {
      const res = await parentsAPI.createParent(parentForm);
      toast.current?.show({ severity: 'success', summary: 'Created', detail: res.data?.message || 'Parent created successfully' });
      setParentDialog(false);
      loadParents();
    } catch (err) {
      console.error('Create parent failed', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to create parent' });
    }
    setLoading(false);
  };
const loadEmployees = async () => {
      try {
        const response = await employeesAPI.getAllEmployees();
        const employees = response.data.data || [];
        setEmployees(employees);
      } catch (err) {
        console.error('Failed to load employees', err);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load employees' });
      }
    };
  const handleDeleteAdmission = (admission) => {
    confirmDialog({
      message: `Delete admission for ${admission.firstName} ${admission.lastName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await admissionsAPI.deleteAdmission(admission.id);
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Admission deleted successfully' });
          loadLazyData();
        } catch (err) {
          console.error('Delete failed', err);
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to delete admission' });
        }
      },
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 mx-5 py-2 rounded-lg transition-all shadow-md',
      rejectClassName: 'bg-gray-400 hover:bg-gray-500 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel'
    });
  };

  const handleViewAdmissionDetails = (admission) => {
    setDetailsAdmission(admission);
    setShowDetailsView(true);
  };

  const handleBackToTable = () => {
    setShowDetailsView(false);
    setDetailsAdmission(null);
  };

  const handleProcessAdmission = (rowData) => {
    setSelectedAdmission(rowData);
  
    setShowProcessAdmissionDialog(true)

    loadParents();
  };
  
  const handleConfirmAdmission = (rowData) => {
    setStudent(rowData);
  setSelectedAdmission(rowData);
  
     setShowAdmissionConfirmDialog(true);
  
   
    loadParents();
  };
 
  // UI helpers
  const getStatusBadge = (status) => (
    <Badge value={status === 'admitted' ? 'Admitted' : 'Draft'} severity={status === 'admitted' ? 'success' : 'warning'} />
  );

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-2 flex-shrink-0">
        <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-500/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full translate-y-12 -translate-x-12"></div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg">
                <i className="pi pi-graduation-cap text-white text-sm"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Admissions Management</h3>
                <p className="text-xs text-gray-600 mt-0.5">Manage student admissions and parent information</p>
              </div>
            </div>

            <div className="flex gap-2">
              {(user?.role === 'admin' || user?.role === 'teacher') && (
                <button onClick={handleCreateAdmission} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl text-sm">
                  <i className="pi pi-plus text-sm"></i> Admit Student
                </button>
              )}
            </div>
          </div>

          {!showDetailsView && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center"><i className="pi pi-search mr-1 text-blue-500 text-xs"></i>Search</label>
                <InputText value={lazyParams.filters?.global?.value || ''} onChange={onGlobalFilterChange} placeholder="search Name..." className="w-full pl-3 pr-3 py-2 text-sm border border-gray-200 rounded-lg" />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center"><i className="pi pi-flag mr-1 text-purple-500 text-xs"></i>Status</label>
                <Dropdown value={lazyParams.filters?.status?.value || ''} options={statusOptions} onChange={(e) => onStatusFilterChange(e.value)} placeholder="Select Status" className="w-full text-sm border border-gray-200 rounded-lg" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0 mt-2">
        <div className="border-b border-gray-100 p-4 flex-shrink-0">
          <div className="space-y-3">
            <div className="flex justify-between ">
              <h3 className="text-2xl font-bold text-gray-900">
                {showDetailsView ? 'Admission Details' : 'Admissions Directory'}
              </h3>

              {!showDetailsView && (totalRecords > 0 ? (
                <span className="flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200 text-sm">
                  <i className="pi pi-check-circle mr-2"></i>
                  {admissions.length} of {totalRecords} Admissions
                </span>
              ) : (
                <span className="flex items-center px-3 py-1 bg-orange-50 text-orange-700 rounded-full border border-orange-200 text-sm">
                  <i className="pi pi-exclamation-triangle mr-2"></i>
                  No admissions found
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading && admissions.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="pi pi-graduation-cap text-blue-600 text-xl"></i>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold text-gray-900">Loading Admissions</span>
                  <span className="text-sm text-gray-500 italic">Please wait while we fetch the directory...</span>
                </div>
              </div>
            </div>
          ) : showDetailsView && detailsAdmission ? (
            <AdmissionDetails
              admission={detailsAdmission}
              handleBackToTable={handleBackToTable}
              getStatusBadge={getStatusBadge}
            />
          ) : (
            <AdmissionsTable
              admissions={admissions}
              lazyParams={lazyParams}
              totalRecords={totalRecords}
              loading={loading}
              onPage={onPage}
              onSort={onSort}
              handleViewAdmissionDetails={handleViewAdmissionDetails}
              handleEditAdmission={handleEditAdmission}
              handleDeleteAdmission={handleDeleteAdmission}
              handleProcessAdmission={handleProcessAdmission}
              handleConfirmAdmission={handleConfirmAdmission}
              getStatusBadge={getStatusBadge}
            />
          )}
        </div>
      </div>

      <ParentFormDialog
        visible={parentDialog}
        onHide={() => setParentDialog(false)}
        editingParent={editingParent}
        parentForm={parentForm}
        setParentForm={setParentForm}
        handleSaveParent={handleSaveParent}
        loading={loading}
      />

      <AdmissionFormDialog
        visible={admissionDialog}
        onHide={() => setAdmissionDialog(false)}
        editingAdmission={editingAdmission}
        admissionForm={admissionForm}
        setAdmissionForm={setAdmissionForm}
        genderOptions={genderOptions}
        referralOptions={referralOptions}
        classes={classes}
        parents={parents}
        employees={employees}
        coupons={coupons}
        loadEmployees={loadEmployees}
        handleSaveAdmission={handleSaveAdmission}
        handleCreateParent={handleCreateParent}
        convertToBase64={convertToBase64}
        loading={loading}
        toast={toast}
      />

      {selectedAdmission && (
        <ProcessAdmissionDialog admission={selectedAdmission} visible={showProcessAdmissionDialog} onHide={() => setShowProcessAdmissionDialog(false)}
          onSuccess={() => loadLazyData()} toastRef={toast} parents={parents} />
      )}
     
     
    </div>
  );
};

export default Admissions;
